import { seedBlogPosts } from '../../data/seedBlogPosts';
import type { BlogPost, BlogPostInput } from '../../types';
import { resolveBlogSlug } from '../../utils/blog';
import { delay, mockStorageKeys, readJson, writeJson } from '../mock/storage';

function ensureBlogPostsSeeded(): BlogPost[] {
  const existing = readJson<BlogPost[] | null>(mockStorageKeys.blogPosts, null);
  if (existing && existing.length > 0) {
    return existing;
  }

  writeJson(mockStorageKeys.blogPosts, seedBlogPosts);
  return seedBlogPosts;
}

function saveBlogPosts(posts: BlogPost[]) {
  writeJson(mockStorageKeys.blogPosts, posts);
}

function nextBlogPostId(posts: BlogPost[]): number {
  return posts.reduce((max, post) => Math.max(max, post.id), 0) + 1;
}

function normalizeBlogPost(input: BlogPostInput, posts: BlogPost[], existingId?: number): BlogPost {
  const now = new Date().toISOString();
  const slug = resolveBlogSlug(input.title, input.slug);
  const publishedAt =
    input.isPublished && !input.publishedAt ? now : input.publishedAt?.trim() || null;

  return {
    id: existingId ?? nextBlogPostId(posts),
    slug,
    title: input.title.trim(),
    excerpt: input.excerpt?.trim() || null,
    coverImage: input.coverImage?.trim() || null,
    content: input.content.trim(),
    author: input.author?.trim() || null,
    isPublished: input.isPublished,
    publishedAt,
    createdAt: existingId
      ? posts.find((post) => post.id === existingId)?.createdAt ?? now
      : now,
    updatedAt: now,
  };
}

export async function mockFetchPublishedBlogPosts(): Promise<BlogPost[]> {
  await delay(200);
  const now = Date.now();
  return ensureBlogPostsSeeded().filter((post) => {
    if (!post.isPublished) return false;
    if (!post.publishedAt) return true;
    return new Date(post.publishedAt).getTime() <= now;
  });
}

export async function mockFetchAllBlogPosts(): Promise<BlogPost[]> {
  await delay(200);
  return ensureBlogPostsSeeded();
}

export async function mockGetBlogPost(id: number): Promise<BlogPost> {
  await delay(150);
  const post = ensureBlogPostsSeeded().find((item) => item.id === id);
  if (!post) {
    throw new Error('Blog yazısı bulunamadı.');
  }
  return post;
}

export async function mockGetBlogPostBySlug(slug: string): Promise<BlogPost> {
  await delay(150);
  const post = (await mockFetchPublishedBlogPosts()).find((item) => item.slug === slug);
  if (!post) {
    throw new Error('Blog yazısı bulunamadı.');
  }
  return post;
}

export async function mockCreateBlogPost(input: BlogPostInput): Promise<BlogPost> {
  await delay();

  const posts = ensureBlogPostsSeeded();
  const post = normalizeBlogPost(input, posts);
  if (posts.some((item) => item.slug === post.slug)) {
    throw new Error('Bu blog slug\'ı zaten kullanılıyor.');
  }

  saveBlogPosts([...posts, post]);
  return post;
}

export async function mockUpdateBlogPost(id: number, input: BlogPostInput): Promise<BlogPost> {
  await delay();

  const posts = ensureBlogPostsSeeded();
  const index = posts.findIndex((item) => item.id === id);
  if (index === -1) {
    throw new Error('Blog yazısı bulunamadı.');
  }

  const post = normalizeBlogPost(input, posts, id);
  if (posts.some((item) => item.slug === post.slug && item.id !== id)) {
    throw new Error('Bu blog slug\'ı zaten kullanılıyor.');
  }

  const next = [...posts];
  next[index] = post;
  saveBlogPosts(next);
  return post;
}

export async function mockUploadBlogMedia(file: File): Promise<string> {
  await delay();
  return URL.createObjectURL(file);
}

export async function mockDeleteBlogPost(id: number): Promise<void> {
  await delay();

  const posts = ensureBlogPostsSeeded();
  const next = posts.filter((item) => item.id !== id);
  if (next.length === posts.length) {
    throw new Error('Blog yazısı bulunamadı.');
  }

  saveBlogPosts(next);
}
